import { useQuery } from '@tanstack/react-query';
import { apiService, type PlanTier } from '../services/api';
import { queryKeys } from '../lib/queryKeys';
import { useAuthContext } from '../contexts/AuthContext';

// Shape returned by usePlanFeatures.
// AB#1933 — the BE `SubscriptionDto` carries planTier only; the feature
// bullet list lives on `SubscriptionPlanDto.Features`. We fetch both and
// project the matching plan's features here so callers stay shape-stable.
export interface PlanFeaturesResult {
  enabledFeatures: string[];
  planTier: PlanTier | undefined;
}

// Fetches the current tenant's active plan features. Joins
// /subscriptions/current (which plan?) with /subscriptions/plans (what
// does that plan include?). Used by RequireFeature component.
// AB#2175 (Batch A) — /subscriptions/current is owner-only; only owners fetch it
// (MainLayout warms this cache on every page, and only owner screens gate on a plan
// feature — staff and Super Admin sessions would just collect 403s).
export const usePlanFeatures = () => {
  const { isSalonOwner } = useAuthContext();
  return useQuery({
    queryKey: queryKeys.subscription.features(),
    queryFn: async (): Promise<PlanFeaturesResult> => {
      const [sub, plans] = await Promise.all([
        apiService.getCurrentSubscription(),
        apiService.getPlans().catch(() => []),
      ]);
      const matching = sub
        ? plans.find(p => p.tier === sub.planTier)
        : undefined;
      return {
        enabledFeatures: matching?.features ?? [],
        planTier: sub?.planTier,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 min — plan changes are infrequent
    enabled: isSalonOwner,
  });
};

export const usePlanFeature = (featureKey: string) => {
  const { data: features, isLoading } = usePlanFeatures();
  return {
    isEnabled: features?.enabledFeatures?.includes(featureKey) ?? false,
    planTier: features?.planTier,
    isLoading,
  };
};
